import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import { PaymentMethod } from '../../services/exports/Constants';
import { stripeElementsExclusions } from '../../utils/payment-options-visibility';
import useGiftCardExcludedPaymentMethods from '../../features/gift-cards/hooks/useGiftCardExcludedPaymentMethods';
import useMobileApp from '../mobile-app/useMobileApp';

/**
 * The Stripe Elements' exclusions for the checkout on screen. An order follows what the merchant hid
 * for its order method; a gift card has none, so only what is hidden everywhere applies, plus the
 * methods a gift card can never be paid with (useGiftCardExcludedPaymentMethods). Inside the native app,
 * the methods that hand off to another app are left out as well.
 */
export default function useStripeElementsExclusions(isGiftCardCheckout: boolean) {
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const giftCardUnavailable = useGiftCardExcludedPaymentMethods();
  const { isMobileApp } = useMobileApp();

  const orderMethod = isGiftCardCheckout ? undefined : cached_order?.method;
  const alsoUnavailable = isGiftCardCheckout
    ? giftCardUnavailable.filter((method) => method !== PaymentMethod.Cash)
    : [];

  return useMemo(
    () => stripeElementsExclusions(company, orderMethod, alsoUnavailable, isMobileApp),
    [
      company?.hidden_payment_methods,
      company?.stripe_excluded_payment_method_types,
      company?.stripe_hideable_payment_method_types,
      orderMethod,
      alsoUnavailable.join(','),
      isMobileApp,
    ],
  );
}
